import React, { useMemo } from 'react';

/*
 * Domain x hat dot matrix. Dot area is proportional to how much work in that
 * domain needed that hat -- the concrete form of the "nine hats" claim.
 *
 * Column headers are horizontal rather than rotated: at this column width the
 * longest label fits, and rotated text overflowed the viewBox.
 */

const GUTTER = 176;
const COL = 68;
const ROW = 44;
const TOP = 46;
const RIGHT = 12;
const MAX_R = 15;

const HatMatrix = ({ themes, domains, hats, activeHat, onToggle }) => {
  const { cells, max } = useMemo(() => {
    const map = {};
    for (const theme of themes) {
      for (const hat of theme.hats) {
        const key = `${theme.domain}|${hat}`;
        map[key] = (map[key] || 0) + theme.weight;
      }
    }
    return { cells: map, max: Math.max(...Object.values(map), 1) };
  }, [themes]);

  const width = GUTTER + hats.length * COL + RIGHT;
  const height = TOP + domains.length * ROW + 14;

  return (
    <div className="hat-matrix">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Which hats each domain demanded">
        {hats.map((hat, c) => {
          const cx = GUTTER + c * COL + COL / 2;
          return (
            <g key={hat.id}>
              <text
                className={`hm-col${activeHat && activeHat !== hat.id ? ' is-dimmed' : ''}`}
                x={cx}
                y={TOP - 20}
                textAnchor="middle"
                onClick={() => onToggle(hat.id)}
              >
                {hat.name}
              </text>
              <text className="hm-col-count" x={cx} y={TOP - 7} textAnchor="middle">
                {hat.count}
              </text>
            </g>
          );
        })}

        {domains.map((domain, r) => {
          const cy = TOP + r * ROW + ROW / 2;
          return (
            <g key={domain.name}>
              <text className="hm-row" x={GUTTER - 16} y={cy} textAnchor="end" dominantBaseline="middle">
                {domain.name}
              </text>
              {hats.map((hat, c) => {
                const value = cells[`${domain.name}|${hat.id}`] || 0;
                if (!value) return null;
                const cx = GUTTER + c * COL + COL / 2;
                const radius = Math.max(4, Math.sqrt(value / max) * MAX_R);
                return (
                  <circle
                    key={hat.id}
                    className={`hm-dot${activeHat && activeHat !== hat.id ? ' is-dimmed' : ''}`}
                    cx={cx}
                    cy={cy}
                    r={radius}
                    style={{ '--dot': `var(--tmc-hat-${hat.id})` }}
                  >
                    <title>{`${domain.name} · ${hat.name}: ${value}`}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default HatMatrix;
